import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Send, Image as ImageIcon, Trash2, Anchor } from "lucide-react";
import { toast } from "sonner";

const SHIFT_LABELS = { "1": "Skift 1", "2": "Skift 2" };
const SHIFT_COLORS = {
  "1": "bg-sailcloth-100 text-navy-800 border-navy-100",
  "2": "bg-blue-50 text-blue-700 border-blue-200",
};

function formatTime(iso) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
    if (sameDay) return `I dag ${time}`;
    return `${d.toLocaleDateString("nb-NO", { day: "2-digit", month: "short" })} ${time}`;
  } catch { return iso; }
}

export default function MessagesPage() {
  const { user, activeBoat } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("all"); // all | 1 | 2
  const endRef = useRef(null);

  const boatId = activeBoat?.id;
  const myShift = user?.preferred_shift || "";
  const isHousehold = activeBoat?.kind === "household";

  const load = useCallback(async () => {
    if (!boatId) return;
    try {
      const res = await api.getBoatMessages(boatId);
      setMessages(res.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error("Kunne ikke laste meldinger");
    }
  }, [boatId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!boatId) return;
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [boatId, load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Maks 5 MB"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.uploadMessageImage(boatId, fd);
      setImage(res.data.image_url);
      toast.success("Bilde valgt");
    } catch { toast.error("Kunne ikke laste opp bilde"); }
  };

  const handleSend = async () => {
    if (!text.trim() && !image) return;
    setSending(true);
    try {
      await api.sendBoatMessage(boatId, { text: text.trim(), image_url: image });
      setText(""); setImage("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
    finally { setSending(false); }
  };

  const handleDelete = async (id) => {
    try { await api.deleteBoatMessage(boatId, id); load(); toast.success("Melding slettet"); }
    catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const visible = filter === "all" ? messages : messages.filter((m) => m.shift === filter);

  if (!boatId) {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center">
        <Anchor className="h-12 w-12 mx-auto text-stone-300 mb-4" />
        <p className="text-stone-500">Velg en båt først for å se meldinger</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto flex flex-col" style={{ height: "calc(100vh - 4rem)" }} data-testid="messages-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Meldinger</h1>
          <p className="text-xs text-stone-500 mt-0.5">{isHousehold ? `Samtale i ${activeBoat?.name}` : `Chat mellom skifta på ${activeBoat?.name}`}</p>
        </div>
        {!isHousehold && (
          <div className="flex gap-1 bg-stone-100 rounded-full p-1" data-testid="messages-filter">
            {[{ v: "all", l: "Alle" }, { v: "1", l: "Skift 1" }, { v: "2", l: "Skift 2" }].map((f) => (
              <button key={f.v} onClick={() => setFilter(f.v)} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${filter === f.v ? "bg-white shadow text-stone-900" : "text-stone-500 hover:text-stone-700"}`} data-testid={`filter-${f.v}`}>
                {f.l}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto bg-stone-50 border border-stone-100 rounded-xl p-4 space-y-3 mb-3" data-testid="messages-list">
        {visible.length === 0 && (
          <div className="text-center py-12 text-stone-400 text-sm">Ingen meldinger ennå. Start samtalen!</div>
        )}
        {visible.map((m) => {
          const mine = m.user_id === user?.user_id;
          const shiftLabel = SHIFT_LABELS[m.shift] || "Uten skift";
          const shiftColor = SHIFT_COLORS[m.shift] || "bg-stone-100 text-stone-600 border-stone-200";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[75%] group ${mine ? "items-end" : "items-start"} flex flex-col`}>
                <div className="flex items-center gap-2 mb-1 px-1">
                  <span className="text-[11px] font-medium text-stone-600">{m.user_name || "Anonym"}</span>
                  {!isHousehold && <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${shiftColor}`}>{shiftLabel}</span>}
                </div>
                <div className={`rounded-2xl px-4 py-2 shadow-sm ${mine ? "bg-navy-800 text-white rounded-br-sm" : "bg-white text-stone-800 border border-stone-200 rounded-bl-sm"}`}>
                  {m.image_url && (
                    <img src={m.image_url} alt="" className="rounded-lg mb-2 max-h-60 w-auto" />
                  )}
                  {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                </div>
                <div className="flex items-center gap-2 mt-1 px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-[10px] text-stone-400">{formatTime(m.created_at)}</span>
                  {mine && (
                    <button onClick={() => handleDelete(m.id)} className="text-stone-400 hover:text-red-500" data-testid={`delete-message-${m.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="bg-white border border-stone-200 rounded-xl p-3 flex items-end gap-2" data-testid="message-composer">
        {image && (
          <div className="relative">
            <img src={image} alt="" className="h-12 w-12 object-cover rounded-lg" />
            <button onClick={() => setImage("")} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] leading-none">×</button>
          </div>
        )}
        <label className="p-2 rounded-lg hover:bg-stone-100 cursor-pointer text-stone-500 hover:text-stone-700 transition-colors" title="Legg ved bilde" data-testid="attach-image-btn">
          <ImageIcon className="h-5 w-5" />
          <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder={isHousehold ? "Skriv en melding til husstanden..." : `Skriv en melding${myShift ? ` som Skift ${myShift}` : ""}...`}
          className="flex-1 resize-none rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
          rows={1}
          style={{ minHeight: 40, maxHeight: 120 }}
          data-testid="message-input"
        />
        <button onClick={handleSend} disabled={sending || (!text.trim() && !image)} className="p-2 rounded-lg bg-navy-800 text-white hover:bg-navy-800 transition-colors disabled:opacity-50" data-testid="send-message-btn">
          <Send className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
